import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Direction_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Bool_Enum } from '@weave/schema-gen-ts/dist/shared/null/types.pb';
import { capitalize } from 'lodash-es';
import { VirtuosoHandle } from 'react-virtuoso';
import { FaxTypes } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { TagsV2 } from '@frontend/api-tag';
import { InfinitePaginatedList, PaginatedList } from '@frontend/components';
import { Headers } from '@frontend/header';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useOnScopeChange } from '@frontend/scope';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { useNavigate, useParams, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  ContentPanel,
  Dot,
  Heading,
  IconButton,
  useFormField,
  useModalControl,
  usePopoverDialog,
  usePopoverMenu,
} from '@frontend/design-system';
import { BulkSelectPopover } from '../components/inbox/bulk-select-popover';
import { DraftInboxListItem } from '../components/inbox/draft-inbox-list-item';
import { FaxStatusFilter } from '../components/inbox/fax-status-filter';
import { FilterPopover } from '../components/inbox/filters-popover';
import { InboxListItem } from '../components/inbox/inbox-list-item';
import { LocationSelectorModal } from '../components/inbox/location-selector-modal';
import { useAccessibleFaxNumbers } from '../hooks/use-accessible-fax-numbers';
import { useListFaxes } from '../hooks/use-list-faxes';
import { useInboxFiltersShallowStore } from '../stores/inbox-filters-store';
import { FaxContactList } from './fax-contacts-list';

const NOTIFICATION_AUTO_SCROLL_MAX_PAGES = 10;

type Props = {
  type?: FaxTypes.InboxType;
  setIsAtEndOfList: (isAtEndOfList: boolean) => void;
};

export const FaxInboxList = ({ type = FaxTypes.InboxType.INBOX, setIsAtEndOfList }: Props) => {
  const { t } = useTranslation('fax');
  const navigate = useNavigate();

  useOnScopeChange(() => {
    resetSelectedFaxes();
    resetInboxFilters();
    navigate({
      to: '/fax/$filter',
      params: { filter: type },
    });
  });

  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const locationId = selectedLocationIds[0];
  const [isBulkSelect, setIsBulkSelect] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedFaxes, setSelectedFaxes] = useState<Record<string, string[]>>({});
  const selectedFaxesCount = Object.values(selectedFaxes).reduce((acc, curr) => acc + curr.length, 0);

  const isBlocked = type === FaxTypes.InboxType.BLOCKED;

  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const isSmallMode = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const checkbox = useFormField({ type: 'checkbox' });
  const { modalProps: locationSelectorModalProps, openModal: openLocationSelectorModal } = useModalControl();
  const bulkPopoverMenu = usePopoverMenu<HTMLButtonElement | HTMLAnchorElement>({ placement: 'bottom' });
  const filtersPopoverMenu = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>({ placement: 'bottom-end' });
  const selectionState = isSelectAll
    ? FaxTypes.BulkSelect.ALL
    : selectedFaxesCount
    ? FaxTypes.BulkSelect.SOME
    : FaxTypes.BulkSelect.NONE;

  const resetSelectedFaxes = () => {
    setSelectedFaxes({});
    setIsSelectAll(false);
    setIsBulkSelect(false);
  };

  const { inboxFilters, resetInboxFilters } = useInboxFiltersShallowStore('inboxFilters', 'resetInboxFilters');

  const { data: faxNumbers } = useAccessibleFaxNumbers(selectedLocationIds);

  const hasFilters = useMemo(
    () =>
      (inboxFilters.tags ?? []).length > 0 ||
      (inboxFilters.faxNumbers ?? []).length > 0 ||
      !!inboxFilters.startDate ||
      !!inboxFilters.endDate ||
      !!inboxFilters.isDelivered,
    [inboxFilters]
  );

  const isViewed =
    inboxFilters.statusFilter === FaxTypes.FaxStatusFilter.UNREAD
      ? Bool_Enum.FALSE
      : inboxFilters.statusFilter === FaxTypes.FaxStatusFilter.READ
      ? Bool_Enum.TRUE
      : undefined;

  const direction =
    inboxFilters.statusFilter === FaxTypes.FaxStatusFilter.RECEIVED
      ? Direction_Enum.INBOUND
      : inboxFilters.statusFilter === FaxTypes.FaxStatusFilter.SENT
      ? Direction_Enum.OUTBOUND
      : undefined;

  const isDelivered =
    inboxFilters.isDelivered === 'delivered'
      ? Bool_Enum.TRUE
      : inboxFilters.isDelivered === 'undelivered'
      ? Bool_Enum.FALSE
      : undefined;

  const formatPhoneNumberWithoutCountryCode = (number: string) => {
    if (!!number && number.startsWith('+1')) return number.slice(2);
    else return number;
  };

  const { paginatedQuery } = useListFaxes({
    locationId,
    locationIds: selectedLocationIds,
    type,
    isViewed,
    isDelivered,
    direction,
    tags: inboxFilters.tags ?? [],
    locationPhones: inboxFilters.faxNumbers?.map((number) => formatPhoneNumberWithoutCountryCode(number)) ?? [],
    startAt: inboxFilters.startDate ? new Date(inboxFilters.startDate).toISOString() : undefined,
    endAt: inboxFilters.endDate ? new Date(inboxFilters.endDate).toISOString() : undefined,
  });

  const { data: listDrafts, isLoading: isLoadingDrafts } = FaxDraftQueries.useListDraftsV2(
    locationId,
    selectedLocationIds,
    {
      enabled: type === FaxTypes.InboxType.DRAFTS,
    }
  );

  const title = type === FaxTypes.InboxType.INBOX ? t('Fax Inbox') : capitalize(type);

  const locationTagsQuery = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
  });

  const handleLocationOnClick = (locationId: string) => {
    navigate({
      to: '/fax/drafts/new',
      search: {
        selectedLocationId: locationId,
      },
    });
  };

  const allFaxes = useMemo(() => {
    return (
      paginatedQuery.data?.pages.flatMap((page) =>
        page.rows?.map((fax) => ({
          id: fax.id,
          locationId: fax.locationId,
          locationPhone: fax.locationPhone,
        }))
      ) ?? []
    );
  }, [JSON.stringify(paginatedQuery.data)]);

  // Scroll the inbox to the fax currently selected from notification
  const { faxId: routeFaxId } = useParams({ from: '/fax/$filter/$faxId', shouldThrow: false }) ?? {};
  const routeSearch = useSearch({ from: '/fax/$filter/$faxId', shouldThrow: false });
  const enteredFromNotification = routeSearch?.isNotification === true;
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const autoFetchedForFaxIdRef = useRef<{ faxId: string | null; pagesFetched: number; scrolled: boolean }>({
    faxId: null,
    pagesFetched: 0,
    scrolled: false,
  });

  useEffect(() => {
    if (
      !routeFaxId ||
      !enteredFromNotification ||
      type === FaxTypes.InboxType.DRAFTS ||
      type === FaxTypes.InboxType.CONTACTS
    ) {
      return;
    }

    // Reset tracking when the target fax changes.
    if (autoFetchedForFaxIdRef.current.faxId !== routeFaxId) {
      autoFetchedForFaxIdRef.current = { faxId: routeFaxId, pagesFetched: 0, scrolled: false };
    }

    const index = allFaxes.findIndex((fax) => fax?.id === routeFaxId);

    if (index >= 0) {
      if (autoFetchedForFaxIdRef.current.scrolled) {
        return;
      }
      autoFetchedForFaxIdRef.current.scrolled = true;
      virtuosoRef.current?.scrollToIndex({ index, align: 'center', behavior: 'auto' });
      return;
    }

    // Not in loaded pages — try to fetch more, up to NOTIFICATION_AUTO_SCROLL_MAX_PAGES.
    const { hasNextPage, isFetchingNextPage, isFetching, fetchNextPage } = paginatedQuery;
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      !isFetching &&
      autoFetchedForFaxIdRef.current.pagesFetched < NOTIFICATION_AUTO_SCROLL_MAX_PAGES
    ) {
      autoFetchedForFaxIdRef.current.pagesFetched += 1;
      fetchNextPage();
    }
  }, [
    routeFaxId,
    enteredFromNotification,
    allFaxes,
    type,
    paginatedQuery.hasNextPage,
    paginatedQuery.isFetchingNextPage,
    paginatedQuery.isFetching,
  ]);

  const selectAllFaxes = () => {
    setSelectedFaxes({});
    setIsSelectAll(true);
  };

  const deselectAllFaxes = () => {
    setSelectedFaxes({});
    setIsSelectAll(false);
  };

  useEffect(() => {
    setSelectedFaxes({});
    setIsSelectAll(false);
    setIsBulkSelect(false);
  }, [type]);

  return (
    <>
      {type === FaxTypes.InboxType.CONTACTS ? (
        <FaxContactList />
      ) : (
        <>
          <Headers.Root hasBottomBorder>
            {isSmallMode && <Headers.RouterBreadcrumb />}
            <Headers.TitleRow>
              <Headers.Title
                css={css`
                  align-items: center;
                  justify-content: space-between;
                `}
              >
                {isBulkSelect && (
                  <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                    }}
                  >
                    <CheckboxField
                      {...checkbox}
                      indeterminate={selectionState === FaxTypes.BulkSelect.SOME}
                      value={selectionState === FaxTypes.BulkSelect.ALL || selectionState === FaxTypes.BulkSelect.SOME}
                      name={selectionState === FaxTypes.BulkSelect.NONE ? t('Select all') : t('Deselect all')}
                      label=''
                      onChange={() => {
                        if (selectionState === FaxTypes.BulkSelect.NONE) {
                          selectAllFaxes();
                        } else {
                          deselectAllFaxes();
                        }
                      }}
                      data-trackingid={`${FaxPrefixes.List}-bulk-select-checkbox`}
                    />
                    <IconButton
                      {...bulkPopoverMenu.getTriggerProps()}
                      label={t('Bulk selection menu')}
                      disabled={selectionState === FaxTypes.BulkSelect.NONE}
                      trackingId={`${FaxPrefixes.List}-bulk-select-menu-button`}
                    >
                      <Icon name='caret-down-small' css={{ color: theme.colors.neutral70 }} />
                    </IconButton>
                  </div>
                )}
                {isBulkSelect ? (
                  <Heading
                    css={{ fontSize: isBulkSelect ? theme.font.size.h3 : theme.font.size.h1 }}
                    data-trackingid={`${FaxPrefixes.List}-title-button`}
                  >
                    {t('Select')}
                  </Heading>
                ) : (
                  <Headers.RouterHeader title={t(`${title}`, { title })} />
                )}
                {isBulkSelect ? (
                  <IconButton
                    label={t('Close bulk select')}
                    showLabelOnHover
                    css={{
                      position: 'relative',
                      padding: 0,
                      minWidth: 36,
                      height: 36,
                      width: 36,
                      border: 'none',
                    }}
                    onClick={() => setIsBulkSelect((prev) => !prev)}
                    trackingId={`${FaxPrefixes.List}-new-message-button`}
                  >
                    <Icon name='x' css={{ color: theme.colors.neutral70 }} />
                  </IconButton>
                ) : (
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    {type !== FaxTypes.InboxType.DRAFTS && (
                      <Headers.SecondaryAction
                        iconName='select-all'
                        trackingId={`${FaxPrefixes.List}-bulk-select-button`}
                        onClick={() => setIsBulkSelect((prev) => !prev)}
                      />
                    )}
                    <Headers.PrimaryAction
                      iconName='draft'
                      onClick={() => {
                        if (selectedLocationIds.length > 1) {
                          openLocationSelectorModal();
                        } else {
                          navigate({
                            to: '/fax/drafts/new',
                          });
                        }
                      }}
                      trackingId={`${FaxPrefixes.List}-new-message-button`}
                    >
                      {t('New Fax')}
                    </Headers.PrimaryAction>
                  </div>
                )}
              </Headers.Title>
            </Headers.TitleRow>
            {type === FaxTypes.InboxType.INBOX && (
              <>
                <Headers.Controls>
                  <IconButton
                    showLabelOnHover
                    trackingId={`${FaxPrefixes.List}-filter-button`}
                    css={{
                      position: 'relative',
                      padding: 0,
                      minWidth: 36,
                      height: 36,
                      width: 36,
                      flexShrink: 0,
                    }}
                    {...filtersPopoverMenu.getTriggerProps()}
                    label={t('Filter faxes')}
                  >
                    <Icon name='filter-alt' css={{ color: theme.colors.neutral70 }} />
                    {hasFilters && (
                      <Dot
                        color='primary'
                        css={{
                          width: theme.spacing(1.5),
                          height: theme.spacing(1.5),
                          top: 2,
                          right: 0,
                          border: `1.5px solid ${theme.colors.white}`,
                        }}
                      />
                    )}
                  </IconButton>
                </Headers.Controls>
                <Headers.Tabs>
                  <div
                    css={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: theme.spacing(0, 2),
                      overflow: 'hidden',
                    }}
                  >
                    <FaxStatusFilter />
                  </div>
                </Headers.Tabs>
              </>
            )}
          </Headers.Root>
          <ContentPanel.Body>
            {type === FaxTypes.InboxType.DRAFTS ? (
              <PaginatedList
                emptyStateConfig={{
                  header: t('No drafts to show'),
                  type: 'fax',
                  disableGraphic: true,
                }}
                listData={listDrafts?.drafts || []}
                renderListItem={DraftInboxListItem}
                isLoading={isLoadingDrafts}
              />
            ) : (
              <InfinitePaginatedList
                virtuosoRef={virtuosoRef}
                infiniteQueryProps={paginatedQuery}
                emptyStateConfig={{
                  header: t('No faxes to show'),
                  disableGraphic: true,
                }}
                renderListItem={({ listItem }) => (
                  <InboxListItem
                    listItem={listItem}
                    type={type}
                    isBulkSelect={isBulkSelect}
                    selectedFaxes={selectedFaxes}
                    setSelectedFaxes={setSelectedFaxes}
                    isSelectAll={isSelectAll}
                    locationTags={locationTagsQuery.data?.tags ?? []}
                    isBlocked={isBlocked}
                    faxNumbers={faxNumbers}
                    isCompactMode={isMobile}
                  />
                )}
                atBottomStateChange={(atBottom) => {
                  const hasNextPage = paginatedQuery?.hasNextPage;
                  const isFetching = paginatedQuery?.isFetching;
                  const hasFetchedFirstPage = paginatedQuery?.isFetched;
                  if (atBottom && !hasNextPage && !isFetching && hasFetchedFirstPage) {
                    setIsAtEndOfList(true);
                  } else {
                    setIsAtEndOfList(false);
                  }
                }}
              />
            )}
          </ContentPanel.Body>
          <BulkSelectPopover
            allFaxes={allFaxes || []}
            selectedFaxes={selectedFaxes}
            resetSelectedFaxes={resetSelectedFaxes}
            isSelectAll={selectionState === FaxTypes.BulkSelect.ALL}
            inboxType={type}
            bulkSelectPopoverMenuProps={bulkPopoverMenu}
            tags={locationTagsQuery.data?.tags ?? []}
          />
          <FilterPopover popoverProps={filtersPopoverMenu} tags={locationTagsQuery.data?.tags ?? []} />
          <LocationSelectorModal
            modalProps={locationSelectorModalProps}
            locationIds={selectedLocationIds}
            onClick={handleLocationOnClick}
          />
        </>
      )}
    </>
  );
};
